import { useState, useCallback, useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { fullArticles } from '@/mocks/article';
import { mainSiteUrl } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const articleData = slug ? fullArticles[slug] : undefined;

  const scrollTo = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  if (!articleData) {
    return <Navigate to="/" replace />;
  }

  const articleUrl = `${import.meta.env.VITE_SITE_URL}/artykul/${articleData.slug}`;
  const siteName = 'Kakobuy Polska';
  const faviconUrl = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png';

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: articleData.h1,
    description: articleData.metaDescription,
    image: articleData.imageUrl,
    datePublished: '2026-01-15T00:00:00+01:00',
    dateModified: '2026-07-21T00:00:00+02:00',
    author: {
      '@type': 'Organization',
      name: siteName,
      url: import.meta.env.VITE_SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: siteName,
      url: import.meta.env.VITE_SITE_URL,
      logo: {
        '@type': 'ImageObject',
        url: faviconUrl,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    inLanguage: 'pl-PL',
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Strona główna',
        item: import.meta.env.VITE_SITE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Poradniki',
        item: `${import.meta.env.VITE_SITE_URL}/artykul/${articleData.slug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: articleData.h1.slice(0, 80),
      },
    ],
  };

  const faqJsonLd = articleData.faq.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: articleData.faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  } : null;

  return (
    <>
      <Navbar />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      {faqJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
        />
      )}

      <main className="pt-20">
        <article className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-4xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-600 transition-colors">Strona główna</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700">Poradniki</span>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-900 font-medium truncate">{articleData.h1.slice(0, 50)}...</span>
            </nav>

            <div className="mb-10">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="text-xs px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 font-medium">Poradnik</span>
                <span className="text-xs text-foreground-500">12 min czytania</span>
                <span className="text-xs text-foreground-500">•</span>
                <span className="text-xs text-foreground-500">Aktualizacja: 2026</span>
              </div>
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 mb-6 leading-tight">
                {articleData.h1}
              </h1>
              <p className="text-sm md:text-lg text-foreground-600 leading-relaxed max-w-3xl">
                {articleData.intro}
              </p>
            </div>

            <div className="h-56 md:h-72 lg:h-80 rounded-lg overflow-hidden mb-12">
              <img
                src={articleData.imageUrl}
                alt={articleData.h1}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="space-y-14">
              {articleData.sections.map((section, sectionIndex) => (
                <section key={sectionIndex}>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-5">
                    {section.heading}
                  </h2>

                  {section.content.map((paragraph, pIndex) => (
                    <p key={pIndex} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                      {paragraph}
                    </p>
                  ))}

                  {section.table && (
                    <div className="mt-6 overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="border-b-2 border-background-300/60">
                            {section.table.headers.map((header, hIndex) => (
                              <th
                                key={hIndex}
                                className={`text-left py-3 px-5 font-semibold ${
                                  hIndex === 0 ? 'text-foreground-900' : 'text-accent-600'
                                }`}
                              >
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {section.table.rows.map((row, rIndex) => (
                            <tr key={rIndex} className="border-b border-background-200/70">
                              {row.map((cell, cIndex) => (
                                <td key={cIndex} className="py-3 px-5 text-foreground-600">{cell}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              ))}
            </div>

            <section className="mt-16 pt-12 border-t border-background-200/70">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
                Często zadawane pytania (FAQ)
              </h2>
              <div className="space-y-3">
                {articleData.faq.map((item, i) => (
                  <div
                    key={i}
                    className="rounded-lg bg-background-100 border border-background-200/70 overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === i ? null : i)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left"
                    >
                      <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">
                        {item.question}
                      </span>
                      <i
                        className={`flex-shrink-0 text-base transition-transform duration-200 ${
                          openFaqIndex === i ? 'rotate-45 ri-add-line text-primary-500' : 'ri-add-line text-foreground-400'
                        }`}
                      ></i>
                    </button>
                    {openFaqIndex === i && (
                      <div className="px-5 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            <div className="mt-16 p-8 md:p-10 rounded-lg bg-primary-50 border border-primary-100 text-center">
              <h3 className="text-lg md:text-xl font-heading font-semibold text-primary-800 mb-3">
                {articleData.cta}
              </h3>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-6">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-all whitespace-nowrap"
                >
                  <i className="ri-file-list-3-line text-base"></i>
                  Kakobuy Spreadsheet
                </a>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-semibold text-background-50 bg-accent-500 hover:bg-accent-600 rounded-lg transition-all whitespace-nowrap"
                >
                  <i className="ri-shopping-cart-line text-base"></i>
                  Buy It Now
                </a>
              </div>
            </div>

            <nav className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
                >
                  <i className="ri-arrow-left-line"></i>
                  Powrót do strony głównej
                </Link>
                <div className="flex flex-wrap gap-3">
                  <Link to="/" className="text-sm text-foreground-500 hover:text-primary-600 transition-colors">
                    Strona główna
                  </Link>
                  <Link to="/#kategorie" className="text-sm text-foreground-500 hover:text-primary-600 transition-colors">
                    Kategorie
                  </Link>
                  <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-sm text-foreground-500 hover:text-primary-600 transition-colors">
                    Spreadsheet
                  </a>
                </div>
              </div>
            </nav>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}